// src/components/Users/AddressSection.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { updateShippingAddress } from '../../redux/slices/Users/userProfileSlice';
import { validateZipcode } from '../../redux/slices/ZipCode/zipCodeSlice';

const EMPTY_ADDRESS = {
  nombre: '',
  paterno: '',
  direccion: '',
  colonia: '',
  delegacion: '',
  zipcode: '',
  estado: '',
  telefono: '',
};

// Cobertura actual del servicio - ajusta esta lista según tus zonas reales.
const ESTADO_OPTIONS = ['CDMX', 'EdoMex'];

const DELEGACIONES_BY_ESTADO = {
  CDMX: [
    'Miguel Hidalgo',
    'Benito Juárez',
    'Cuauhtémoc',
    'Huixquilucan',
    'Álvaro Obregón',
    'Cuajimalpa',
    'Azcapotzalco',
  ],
  EdoMex: ['Tlalnepantla', 'Naucalpan', 'Atizapán', 'Metepec'],
};

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    <path d="M12.001 2C6.478 2 2 6.477 2 12c0 1.802.472 3.545 1.369 5.081L2 22l5.058-1.326A9.958 9.958 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12.001 2zm0 18.15a8.13 8.13 0 01-4.146-1.135l-.297-.176-3.006.788.803-2.93-.193-.301A8.14 8.14 0 013.85 12c0-4.5 3.65-8.15 8.15-8.15S20.15 7.5 20.15 12s-3.65 8.15-8.149 8.15z" />
  </svg>
);

export default function AddressSection({ profile }) {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(profile?.shippingAddress || EMPTY_ADDRESS);
  const [submitting, setSubmitting] = useState(false);

  const { isLoading: checkingZip } = useSelector((state) => state.zipcode);
  const [zipStatus, setZipStatus] = useState(null); // null | 'valid' | 'invalid'

  const hasAddress = profile?.hasShippingAddress;
  const formVisible = !hasAddress || editing;

  useEffect(() => {
    if (!formVisible) return;
    const zip = form.zipcode?.trim();
    if (!zip || zip.length !== 5) {
      setZipStatus(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await dispatch(validateZipcode(zip)).unwrap();
        setZipStatus(result.valid ? 'valid' : 'invalid');
      } catch {
        setZipStatus('invalid');
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.zipcode, formVisible, dispatch]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Si cambia el estado, la delegación seleccionada puede dejar de ser
  // válida para el nuevo estado - la reseteamos para no guardar una
  // combinación inconsistente (ej. "Naucalpan" con estado "CDMX").
  const handleEstadoChange = (e) => {
    const estado = e.target.value;
    setForm((prev) => ({
      ...prev,
      estado,
      delegacion: DELEGACIONES_BY_ESTADO[estado]?.includes(prev.delegacion) ? prev.delegacion : '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await dispatch(updateShippingAddress(form)).unwrap();
      toast.success(t('address.saved'));
      setEditing(false);
    } catch (err) {
      toast.error(err || t('address.saveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-2.5 border border-[#3A2A20]/15 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#9A0002]/40 focus:border-[#9A0002] transition text-sm';
  const labelClass = 'block text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/60 mb-1.5';

  const delegacionOptions = DELEGACIONES_BY_ESTADO[form.estado] || [];

  return (
    <div className="bg-white rounded-2xl border border-[#3A2A20]/10 overflow-hidden mb-8">
      <div className="px-6 py-4 border-b border-[#3A2A20]/10 flex justify-between items-center">
        <h2 className="font-['Fraunces'] text-xl font-semibold text-[#3A2A20]">
          {t('address.title')}
        </h2>
        {hasAddress && !editing && (
          <button
            onClick={() => {
              setForm(profile.shippingAddress);
              setEditing(true);
            }}
            className="text-sm font-medium text-[#9A0002] hover:text-[#7A0001]"
          >
            {t('address.edit')}
          </button>
        )}
      </div>

      <div className="p-6">
        {hasAddress && !editing ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <p className="text-[#3A2A20]">
              <span className="text-[#3A2A20]/50">{t('address.nombre')}: </span>
              {profile.shippingAddress.nombre} {profile.shippingAddress.paterno}
            </p>
            <p className="text-[#3A2A20] flex items-center gap-1.5">
              <span className="text-[#3A2A20]/50">{t('address.telefono')}: </span>
              <WhatsAppIcon className="h-3.5 w-3.5 text-[#3A6B4C] shrink-0" />
              {profile.shippingAddress.telefono}
            </p>
            <p className="text-[#3A2A20] md:col-span-2">
              <span className="text-[#3A2A20]/50">{t('address.direccion')}: </span>
              {profile.shippingAddress.direccion}, {profile.shippingAddress.colonia}
            </p>
            <p className="text-[#3A2A20]">
              <span className="text-[#3A2A20]/50">{t('address.delegacion')}: </span>
              {profile.shippingAddress.delegacion}
            </p>
            <p className="text-[#3A2A20]">
              <span className="text-[#3A2A20]/50">{t('address.estado')}: </span>
              {profile.shippingAddress.estado}, {profile.shippingAddress.zipcode}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>{t('address.nombre')}</label>
              <input
                type="text"
                name="nombre"
                value={form.nombre}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>{t('address.paterno')}</label>
              <input
                type="text"
                name="paterno"
                value={form.paterno}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelClass}>{t('address.direccion')}</label>
              <input
                type="text"
                name="direccion"
                value={form.direccion}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>{t('address.colonia')}</label>
              <input
                type="text"
                name="colonia"
                value={form.colonia}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>

            {/* Estado - dropdown limitado a las 2 zonas de cobertura */}
            <div>
              <label className={labelClass}>{t('address.estado')}</label>
              <select
                name="estado"
                value={form.estado}
                onChange={handleEstadoChange}
                required
                className={inputClass}
              >
                <option value="">{t('address.selectOption')}</option>
                {ESTADO_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            {/* Delegación - depende del estado elegido */}
            <div>
              <label className={labelClass}>{t('address.delegacion')}</label>
              <select
                name="delegacion"
                value={form.delegacion}
                onChange={handleChange}
                required
                disabled={!form.estado}
                className={`${inputClass} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <option value="">
                  {form.estado ? t('address.selectOption') : t('address.selectEstadoFirst')}
                </option>
                {delegacionOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            {/* Zipcode con validación en línea */}
            <div>
              <label className={labelClass}>{t('address.zipcode')}</label>
              <input
                type="text"
                name="zipcode"
                value={form.zipcode}
                onChange={handleChange}
                required
                className={inputClass}
              />
              <div className="h-4 mt-1.5">
                {checkingZip && (
                  <span className="text-xs text-[#3A2A20]/40">{t('zipcode.checking')}</span>
                )}
                {!checkingZip && zipStatus === 'valid' && (
                  <span className="text-xs text-[#3A6B4C]">✓ {t('zipcode.success')}</span>
                )}
                {!checkingZip && zipStatus === 'invalid' && (
                  <span className="text-xs text-[#9A0002]">{t('zipcode.error')}</span>
                )}
              </div>
            </div>

            {/* Teléfono - más angosto, con ícono de WhatsApp y aviso de lada */}
            <div className="max-w-[220px]">
              <label className={labelClass}>{t('address.telefono')}</label>
              <div className="relative">
                <WhatsAppIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3A6B4C]" />
                <input
                  type="tel"
                  name="telefono"
                  value={form.telefono}
                  onChange={handleChange}
                  required
                  placeholder="5512345678"
                  className={`${inputClass} pl-9`}
                />
              </div>
              <p className="text-xs text-[#3A2A20]/40 mt-1.5">{t('address.phoneHint')}</p>
            </div>

            <div className="md:col-span-2 flex justify-end gap-3 mt-2">
              {editing && (
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="px-5 py-2.5 text-sm font-medium text-[#3A2A20] border border-[#3A2A20]/20 rounded-xl hover:bg-[#3A2A20]/5 transition"
                >
                  {t('address.cancel')}
                </button>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2.5 text-sm font-medium bg-[#9A0002] hover:bg-[#7A0001] text-[#EFE6DD] rounded-xl transition disabled:opacity-50"
              >
                {submitting ? t('address.saving') : t('address.save')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}